// React Imports
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// MUI Imports
import {
  IconButton,
  Button,
  Box,
  Typography,
  Card,
  CardHeader,
  MenuItem,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Backdrop,
  Popover,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import Divider from "@mui/material/Divider";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro , useGridApiRef} from '@mui/x-data-grid-pro';

// ** Icon Imports
import Icon from "../../../components/icon";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

// ** Custom Component Import
import CustomTextField from "../../../components/mui/text-field";

// ** Layout Import
import UserLayout from "../../../components/layouts/UserLayout";

// ** Dialog Imports
// import TagManagementDialog from "../Customers/component/TagManagement";
// import ConvertDialog from "./component/Convert";
import RecordPaymentInvoiceDialog from "./RecordPaymentInvoice";
import RecordPaymentAdvanceDialog from "./RecordPaymentAdvance";
import PaymentDetailDialog from './PaymentDetail';
import RefundDialog from "./Refund";

import { useIndexLogic } from "../logic/index-logic";
import PaymentDetail from "./PaymentDetail";
import { ToastContainer, toast } from "react-toastify";
// import AdvancedFilter from "../Requests/component/AdvancedFilter";
import { usePaymentPermission } from "../permissions/PaymentPermissions";
import ImportExportOptions from "components/ImportExportOptions";

const dropdownItemsStyle = {
  px: 6,
  py: 2,
  "&:hover": {
    backgroundColor: "action.hover",
    cursor: "pointer",
  },
};

//#region Custom Styled Components
const PopoverButton = ({ invoiceID, handleOpenInvoice, handleOpenAdvPayment, hasPaymentSavePermission, invoiceStatus }) => {
  
  const [anchorEl, setAnchorEl] = useState(null);

  if (invoiceID > 0) {
    return (
      <Button
        variant="contained"
        color="main"
         sx={{
           width: { xs: "100%", sm: "auto" },
                    }}
        endIcon={<AddIcon />}
        disabled={!hasPaymentSavePermission || invoiceStatus === "Cancelled" || invoiceStatus === "Paid"}
        onClick={() => handleOpenInvoice(invoiceID)}
      >
        Take a Payment
      </Button>
    );
  }

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // const handleInvoiceClick = () => {
  //   handleClose();
  //   handleOpenInvoice();
  // };

  const handleQuickPaymentClick = () => {
    handleClose();
    handleOpenAdvPayment();
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <div>
      <Button
        variant="contained"
        color="main"
        endIcon={<AddIcon />}
        aria-describedby={id}
        disabled={!hasPaymentSavePermission}
        {...(hasPaymentSavePermission && {
          onClick: handleClick
        })}
      >
        Take a Payment
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ py: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h6"
              sx={dropdownItemsStyle}
              onClick={() => {
                handleClose();
                handleOpenInvoice(invoiceID);  // pass the ID along
              }}
            >
              Invoice
            </Typography>
            {invoiceID === 0 && (
            <Typography
              variant="h6"
              sx={dropdownItemsStyle}
              onClick={() => {
                handleClose();
                handleOpenAdvPayment();
              }}
            >
              Quick Payment
            </Typography>
          )}
          </Box>
        </Box>
      </Popover>
    </div>
  );
};
//#endregion Custom Styled Components

const PaymentsComponent = ({
  invoiceID,
  invoiceStatus = ""
}) => {
  //#region Permissions
  const { PaymentPermissionProps } = usePaymentPermission();
  const {
    hasPaymentSavePermission,
    hasPaymentGetPermission,
    hasPaymentDeletePermission
  } = PaymentPermissionProps;
  //#endregion Permissions

  const { IndexProps, RefundProps } = useIndexLogic();
  const navigate = useNavigate();

  const {
    columns,
    openDetail,
    paymentStatus,
    entity,
    paymentID,
    handleCloseDetailDlg,
    openInvoicePayment,
    handleOpenInvoicePayment,
    handleCloseInvoicePayment,
    openAdvPayment,
    handleOpenAdvPayment,
    handleCloseAdvPayment,
    handleOpenTagManagementDlg,
    handleCloseTagManagementDlg,
    handleClearSelection,
    handleGridSelection,
    handleSearch,
    handleClearSearch,
    loading,
    setLoading,
    filteredRows,
    searchText,
    data,
    paginationText,
    paginationModel,
    setPaginationModel,
    handlePaginationModel,
    selectedRows,
    rowSelectionModel,
    setRowSelectionModel,
    openTagManagementDlg,
    tagManagementData,
    fetchPayments,
    selectedPaymentRow
  } = IndexProps;

   const apiRef = useGridApiRef();
  
  useEffect(() => {
    if (hasPaymentGetPermission) {
      fetchPayments(invoiceID);
    }
  }, [fetchPayments, hasPaymentGetPermission, invoiceID]);

  const visibleColumns = invoiceID > 0
  ? columns.filter(col => col.field !== 'WO_ID')
  : columns;

        useEffect(() => {
           if (!apiRef.current) return;
       
           apiRef.current.autosizeColumns({
             includeHeaders: true,
             includeOutliers: true,
             expand: false,
           });
         }, [ filteredRows,visibleColumns, apiRef]);
  
  return (
    <>
      <Card>
        <CardHeader
          title={
             <Box sx={{ mb: { xs: 3, sm: 0 } }}> 
      <Box sx={{ 
        border: '1px solid #D9D9D9', 
        borderRadius: 1, 
        px: 4, 
        py: 2, 
        maxWidth: 'max-content',
        width: { xs: '100%', sm: 'auto' } 
      }}>
                <Typography variant="body2" sx={{pointerEvents: 'none'}}>
                  {filteredRows.length === (data || []).length
                    ? `Displaying ${paginationText} records`
                    : `Displaying ${paginationText} records`}
                </Typography>
              </Box>
            </Box>
          }
          action={
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <CustomTextField
                value={searchText}
                placeholder="Search"
                disabled={!hasPaymentGetPermission}
                {...(hasPaymentGetPermission && {
                  onChange: (e) => handleSearch(e.target.value)
                })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon="tabler:search" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchText && (
                        <IconButton
                          edge="end"
                          onClick={handleClearSearch}
                          aria-label="clear"
                          sx={{ mr: 2 }}
                        >
                          <Icon icon="tabler:x" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <PopoverButton
                invoiceID={invoiceID}
                handleOpenInvoice={handleOpenInvoicePayment}
                handleOpenAdvPayment={handleOpenAdvPayment}
                hasPaymentSavePermission={hasPaymentSavePermission}
                invoiceStatus={invoiceStatus}
              />
              <ImportExportOptions 
                handleOpenBulkImportDlg={()=>{}}                          
                data={filteredRows}
                module="Payments"
              />              
            </Box>
          }
          sx={{
            py: 4,
            flexDirection: ["column", "row"],
            "& .MuiCardHeader-action": { m: 0 },
            alignItems: ["flex-start", "center"],
          }}
        />

        <Box sx={{ height: '75vh' }}>
          <DataGridPro
            // key={collapse ? "collapsed" : "expanded"}
            // pagination
            // pageSizeOptions={[10, 50, { value: filteredRows.length, label: "Show All" }]}
            apiRef={apiRef}
            rows={filteredRows}
            columns={visibleColumns}
            rowHeight={62}
            // paginationModel={paginationModel}
            // onPaginationModelChange={handlePaginationModel}
            // rows={rowsAssignedTasks}
            disableRowSelectionOnClick
            slots={{
              footer: () => null
            }}
            // loading={loading}
            sx={{
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "none",
              },
              maxHeight: '750vh',
            }}
          />
        </Box>
      </Card>

      {/* Pop up dialogs */}

      <PaymentDetailDialog
        open={openDetail}
        handleClose={handleCloseDetailDlg}
        status={paymentStatus}
        entity={entity}
        paymentID={paymentID}
        RefundProps={RefundProps}
        payment={selectedPaymentRow}
        fetchPayments={fetchPayments}
        invoiceID={invoiceID}
      />

      <RecordPaymentInvoiceDialog
        open={openInvoicePayment}
        handleClose={handleCloseInvoicePayment}
        fetchPayments={fetchPayments}
        invoiceID={invoiceID}
      />

      <RecordPaymentAdvanceDialog
        open={openAdvPayment}
        handleClose={handleCloseAdvPayment}
        fetchPayments={fetchPayments}
      />

      <RefundDialog 
      //  RefundProps={{...RefundProps, refundData: selectedPaymentRow}}
       RefundProps={RefundProps}
       fetchPayments={fetchPayments}
       invoiceID={invoiceID}
      />

      {/* Convert Dialog */}
      {/* <ConvertDialog 
        open={openScheduleAst}
        handleClose={handleCloseScheduleAstDlg}
      /> */}

      {/* Tag Management Dialog */}
      {/* <TagManagementDialog
        open={openTagManagementDlg}
        handleClose={handleCloseTagManagementDlg}
        data={tagManagementData}
      /> */}

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1000 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>

  );
};

export default PaymentsComponent;
